<template>
  <div
    class="top-cards"
    :class="[`top-cards--rem3-${cardCount % 3}`, `top-cards--rem2-${cardCount % 2}`, { 'top-cards--share': !!shareSteamId }]"
    :style="{ '--top-fr-cols': shareSteamId ? cardCount - 1 : cardCount }"
    role="complementary"
  >
    <div
      v-if="hasProfileCard"
      class="profile-card top-card"
      :aria-label="`${$t('summary.profile')} — ${profile.display_name || $t('summary.steamUser')}`"
    >
      <button
        v-if="canExit"
        type="button"
        class="profile-card-exit"
        :title="$t('summary.exitProfile')"
        :aria-label="$t('summary.exitProfile')"
        @click="$emit('exit-profile')"
      >✕</button>
      <div class="profile-card-body">
        <img
          v-if="profile.avatar_url"
          :src="profile.avatar_url"
          alt=""
          class="profile-card-avatar"
          @error="onAvatarError"
        />
        <div class="profile-card-meta">
          <div class="card-label">{{ $t('summary.profile') }}</div>
          <h1 class="profile-card-name" :title="profile.display_name">
            <a
              v-if="profileLink"
              :href="profileLink"
              target="_blank"
              rel="noopener noreferrer"
              class="profile-card-name--link"
            >{{ profile.display_name || $t('summary.steamUser') }}</a>
            <template v-else>{{ profile.display_name || $t('summary.steamUser') }}</template>
          </h1>
        </div>
      </div>
      <!-- Outside .profile-card-meta on purpose: in there it shares a row with
           the 48px avatar and gets ~75px on a phone, which made the button hang
           over the next card. As a sibling it spans the whole card. -->
      <ClaimCard :profile-steam-id="profileSteamId" variant="compact" />
    </div>

    <div class="filters-card top-card" :aria-label="$t('summary.dateRange')">
      <div class="card-icon" aria-hidden="true">🔍</div>
      <div class="card-label">{{ $t('summary.dateRange') }}</div>
      <div class="filter-group">
        <label
          for="date-range-select"
          class="range-label"
        >
          <span class="range-window">{{ rangeLabelText || $t('summary.selectRange') }}</span>
        </label>
        <select
          id="date-range-select"
          :value="dateRangeType"
          :aria-label="$t('summary.selectDateRange')"
          @change="handleRangeChange"
        >
          <option
            v-for="option in resolvedDateRangeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>

    <SummaryMetricCard
      v-for="metric in metrics"
      :key="metric.id"
      :icon="metric.icon"
      :label="metric.label"
      :value="metric.value"
      :sub="metric.sub"
      :image-url="metric.imageUrl || ''"
      :loading="suspendCharts"
    />

    <ShareCard
      v-if="shareSteamId"
      :steam-id="shareSteamId"
    />
  </div>
</template>

<script>
import ClaimCard from './ClaimCard.vue'
import ShareCard from './ShareCard.vue'
import SummaryMetricCard from './SummaryMetricCard.vue'

export default {
  name: 'DashboardSummarySection',
  components: {
    ClaimCard,
    ShareCard,
    SummaryMetricCard
  },
  props: {
    dateRangeType: {
      type: String,
      default: '30d'
    },
    suspendCharts: {
      type: Boolean,
      default: false
    },
    metrics: {
      type: Array,
      default: () => []
    },
    dateRangeOptions: {
      type: Array,
      default: () => []
    },
    rangeStartLabel: {
      type: String,
      default: ''
    },
    rangeEndLabel: {
      type: String,
      default: ''
    },
    profile: {
      type: Object,
      default: null
    },
    profileSteamId: {
      type: String,
      default: ''
    },
    canExit: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:dateRangeType', 'exit-profile'],
  computed: {
    resolvedDateRangeOptions() {
      if (this.dateRangeOptions.length) {
        return this.dateRangeOptions
      }
      return [
        { value: '7d', label: this.$t('summary.ranges.lastWeek') },
        { value: '14d', label: this.$t('summary.ranges.lastTwoWeeks') },
        { value: '30d', label: this.$t('summary.ranges.last30') },
        { value: '90d', label: this.$t('summary.ranges.last90') },
        { value: '365d', label: this.$t('summary.ranges.last365') },
        { value: 'thisWeek', label: this.$t('summary.ranges.thisWeek') },
        { value: 'thisMonth', label: this.$t('summary.ranges.thisMonth') }
      ]
    },
    rangeLabelText() {
      const start = (this.rangeStartLabel || '').trim()
      const end = (this.rangeEndLabel || '').trim()
      if (start && end) {
        return `${start} – ${end}`
      }
      return start || end || ''
    },
    profileLink() {
      const url = this.profile?.profile_url
      if (typeof url === 'string' && url) return url
      const id = (this.profileSteamId || this.profile?.steam_id || '').toString().trim()
      if (/^\d{17}$/.test(id)) return `https://steamcommunity.com/profiles/${id}`
      return ''
    },
    hasProfileCard() {
      return !!(this.profile && (this.profile.display_name || this.profile.avatar_url))
    },
    // Cards actually rendered in the row — drives the column count and which
    // card fills a short last row. Must match the v-ifs in the template.
    cardCount() {
      return (this.hasProfileCard ? 1 : 0) + 1 + this.metrics.length + (this.shareSteamId ? 1 : 0)
    },
    shareSteamId() {
      const id = (this.profileSteamId || this.profile?.steam_id || '').toString().trim()
      return /^\d{17}$/.test(id) ? id : ''
    }
  },
  methods: {
    handleRangeChange(event) {
      const value = event?.target?.value || ''
      this.$emit('update:dateRangeType', value)
    },
    onAvatarError(e) {
      const el = e?.target
      if (el) el.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.top-cards {
  display: grid;
  /* One row on wide screens: equal cards, plus a content-width share card so
     its icon row never squeezes the others. The count comes from the
     component — it is 5 or 6 depending on whether All-Time has a value. */
  grid-template-columns: repeat(var(--top-fr-cols, 4), minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.top-cards--share {
  grid-template-columns: repeat(var(--top-fr-cols, 4), minmax(0, 1fr)) auto;
}

.top-card {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 96px;
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.2s ease;
}

.top-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary), rgba(102, 192, 244, 0.25));
  border-radius: 8px 8px 0 0;
}

.top-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  border-color: rgba(42, 71, 94, 0.9);
}

.card-icon {
  font-size: 1.25rem;
  line-height: 1;
  margin-bottom: 8px;
  color: var(--primary);
}

.card-label {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.filter-group {
  margin-bottom: 0;
}

/* Profile card — first slot in the top-cards row. Avatar left, name right,
   exit ✕ as an absolutely-positioned chip in the top-right. Same chrome as
   the other top-cards via `.top-card` (gradient accent, hover lift). */
.profile-card-body {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.profile-card-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(102, 192, 244, 0.35);
  flex-shrink: 0;
}

.profile-card-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.profile-card-name {
  margin: 0;
  color: var(--text);
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-card-name--link {
  color: inherit;
  text-decoration: none;
  transition: color 0.15s ease;
}

.profile-card-name--link:hover {
  color: var(--primary);
}

.profile-card-exit {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(8, 12, 20, 0.55);
  color: var(--muted);
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.profile-card-exit:hover {
  background: rgba(217, 83, 79, 0.2);
  color: var(--text);
}

.range-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: var(--text);
  font-size: 0.9rem;
  margin-bottom: 6px;
}

.range-window {
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 600;
}

.filter-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--panel-border);
  border-radius: 6px;
  font-size: 0.95rem;
  background: #0f141a;
  color: var(--text);
}

/* Below one-row width the grid gets a fixed column count, and the last card
   stretches over whatever a short last row leaves empty, so no size leaves a
   hole. (Child component roots carry this scope id too, so :last-child
   reaches ShareCard / SummaryMetricCard.) */
@media (max-width: 1359px) {
  .top-cards,
  .top-cards--share {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .top-cards--rem3-1 > :last-child { grid-column: span 3; }
  .top-cards--rem3-2 > :last-child { grid-column: span 2; }
}

@media (max-width: 899px) {
  .top-cards,
  .top-cards--share {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .top-cards--rem3-1 > :last-child,
  .top-cards--rem3-2 > :last-child { grid-column: auto; }
  .top-cards--rem2-1 > :last-child { grid-column: 1 / -1; }
}
</style>
